// src/pages/AddListing/ListingForm.jsx
import React from 'react';
import { addListingStyles } from './styles';
import ImageUpload from './ImageUpload';
import { CATEGORY_OPTIONS } from '../../constants';

// Modern SVG Icons
const TitleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 7V4h16v3M9 20h6M12 4v16" />
  </svg>
);

const DescriptionIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const PriceIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="16" />
    <path d="M15 9.5a3.5 3.5 0 0 0-7 0M15 14.5a3.5 3.5 0 0 1-7 0" />
  </svg>
);

const CategoryIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
  </svg>
);

const QuantityIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <line x1="3" y1="9" x2="21" y2="9" />
    <line x1="9" y1="21" x2="9" y2="9" />
  </svg>
);

const PublishIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const ListingForm = ({ 
  formData, 
  handleChange, 
  handleSubmit, 
  files, 
  setFiles,
  previews,
  setPreviews,
  error, 
  setError,
  loading 
}) => {
  return (
    <div style={addListingStyles.pageContainer}>
      <div style={addListingStyles.contentWrapper}>
        {/* Header Section */}
        <div style={addListingStyles.header}>
          <h1 style={addListingStyles.pageTitle}>Create New Listing</h1>
          <p style={addListingStyles.pageSubtitle}>
            Share your item with the Illinois State University community
          </p>
        </div>

        {error && (
          <div style={addListingStyles.errorMessage}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ minWidth: '20px' }}>
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} style={addListingStyles.form}>
          {/* Listing Details Card */}
          <div style={addListingStyles.formCard}>
            <h2 style={addListingStyles.cardTitle}>Listing Details</h2>
            
            <div style={addListingStyles.inputGroup}>
              <label style={addListingStyles.label}>
                <TitleIcon />
                <span>Title</span>
              </label>
              <input
                type="text"
                name="title"
                placeholder="Enter a descriptive title for your item"
                value={formData.title}
                onChange={handleChange}
                required
                style={addListingStyles.input}
              />
            </div>

            <div style={addListingStyles.inputGroup}>
              <label style={addListingStyles.label}>
                <DescriptionIcon />
                <span>Description</span>
              </label>
              <textarea
                name="description"
                placeholder="Describe your item, its condition, and any relevant details"
                value={formData.description}
                onChange={handleChange}
                required
                style={addListingStyles.textarea}
              />
            </div>

            <div style={addListingStyles.inputRow}>
              <div style={addListingStyles.inputGroup}>
                <label style={addListingStyles.label}>
                  <PriceIcon />
                  <span>Price (per item)</span>
                </label>
                <input
                  type="number"
                  name="price"
                  placeholder="0.00"
                  value={formData.price}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  style={addListingStyles.input}
                />
              </div>

              <div style={addListingStyles.inputGroup}>
                <label style={addListingStyles.label}>
                  <QuantityIcon />
                  <span>Quantity Available</span>
                </label>
                <input
                  type="number"
                  name="quantity"
                  placeholder="1"
                  value={formData.quantity}
                  onChange={handleChange}
                  required
                  min="1"
                  step="1"
                  style={addListingStyles.input}
                />
              </div>
            </div>

            <div style={addListingStyles.inputGroup}>
              <label style={addListingStyles.label}>
                <CategoryIcon />
                <span>Category</span>
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                style={{
                  ...addListingStyles.input,
                  cursor: 'pointer',
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23CE1126' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 16px center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '20px',
                  paddingRight: '48px',
                }}
              >
                <option value="">Select a category</option>
                {CATEGORY_OPTIONS.map((option, index) => 
                  option.isHeader ? (
                    <option key={index} disabled style={{ 
                      fontWeight: 'bold', 
                      color: '#CE1126',
                      fontSize: '14px',
                      backgroundColor: '#f5f5f5'
                    }}>
                      {option.label}
                    </option>
                  ) : (
                    <option key={index} value={option.value}>
                      {option.label}
                    </option>
                  )
                )}
              </select>
            </div>
          </div>

          {/* Image Upload Card */}
          <div style={addListingStyles.formCard}>
            <h2 style={addListingStyles.cardTitle}>Photos</h2>
            <ImageUpload 
              files={files}
              setFiles={setFiles}
              previews={previews}
              setPreviews={setPreviews}
              error={error}
              setError={setError}
              loading={loading}
            />
          </div>

          {/* Submit Button */}
          <button 
            type="submit" 
            disabled={loading}
            style={{
              ...addListingStyles.submitButton,
              ...(loading ? addListingStyles.submitButtonDisabled : {})
            }}
            onMouseEnter={(e) => !loading && (e.currentTarget.style.transform = 'translateY(-2px)')}
            onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
          >
            <PublishIcon />
            <span>{loading ? "Publishing Listing..." : "Publish Listing"}</span>
          </button>
        </form>
      </div>
    </div>
  );
};

export default ListingForm;